import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { Phone, Navigation, ArrowRight, ArrowUpRight, Instagram, Megaphone, MapPin, Clock } from "lucide-react";
import { api } from "../lib/api";
import { bust } from "../lib/img";
import { useSite } from "../context/SiteContext";
import { events } from "../lib/analytics";
import { Reveal, MaskedLines } from "../components/Reveal";
import { MenuItemCard } from "../components/MenuItemCard";

const manifesto = [
  { num: "01", titleKey: "m1Title", stKey: "quality_text" },
  { num: "02", titleKey: "m2Title", stKey: "vision_text" },
  { num: "03", titleKey: "m3Title", tKey: "guestBody" },
];

const CATEGORY_TILES = {
  "ana-yemekler": { img: "/images/karisik-izgara.jpg", span: "lg:col-span-7 lg:row-span-2 lg:min-h-[38rem]" },
  "tatlilar": { img: "/images/kunefe.jpg", span: "lg:col-span-5" },
  "soguk-icecekler": { img: "/images/frozen.jpg", span: "lg:col-span-3" },
  "sicak-icecekler": { img: "/images/turk-kahvesi.jpg", span: "lg:col-span-2" },
};

export default function Home() {
  const { settings, t, st, img, localName } = useSite();
  const [featured, setFeatured] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [menu, setMenu] = useState([]);
  const s = settings || {};
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  useEffect(() => {
    api.get("/featured").then((r) => setFeatured(r.data)).catch(() => {});
    api.get("/campaigns").then((r) => setCampaigns(r.data)).catch(() => {});
    api.get("/menu").then((r) => setMenu(r.data)).catch(() => {});
  }, []);

  const phoneHref = s.phone ? `tel:${s.phone.replace(/[^\d+]/g, "")}` : "#";
  const shortAddress = s.address ? s.address.split(",").slice(-2).join(",").trim() : "";
  const ft = t("featuredTitle").split(" ");

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[100svh] flex flex-col justify-end overflow-hidden" data-testid="hero-section">
        <motion.div className="absolute inset-0" style={{ y: bgY }}>
          <img src={bust(img("hero_image", "/images/kumpir.jpg"))} alt="Cafe Del Nord taş fırında kumpir" className="w-full h-full object-cover kenburns" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#080706] via-[#080706]/55 to-[#080706]/25" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#080706]/80 via-transparent to-transparent" />
        </motion.div>

        <div className="hidden lg:flex absolute left-8 top-1/2 -translate-y-1/2 items-center gap-4 rotate-180 [writing-mode:vertical-rl] text-[0.65rem] tracking-[0.4em] uppercase text-white/40 font-display">
          <span>Venezia · İstanbul</span><span className="w-px h-16 bg-white/20" /><span>Cafe Del Nord</span>
        </div>

        <motion.div style={{ opacity: heroOpacity }} className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 w-full pb-14 sm:pb-20 pt-40">
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.15 }} className="eyebrow !text-base mb-6" data-testid="hero-eyebrow">
            <span className="kicker-line" />{(st("tagline") || "Bir cafeden daha fazlası").toUpperCase()}
          </motion.p>

          <h1 className="font-display font-black tracking-tighter leading-[0.9] text-[15vw] sm:text-[9.5vw] lg:text-[7.2rem] xl:text-[8.4rem] max-w-6xl" data-testid="hero-title">
            <MaskedLines key={t("heroLine1")} lines={[t("heroLine1")]} delay={0.25} />
            <span className="block font-serif-editorial italic font-normal tracking-normal text-gold-grad text-[13vw] sm:text-[8.5vw] lg:text-[6.6rem] xl:text-[7.6rem] -mt-1">
              <MaskedLines key={t("heroLine2")} lines={[t("heroLine2")]} delay={0.45} />
            </span>
          </h1>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mt-10">
            <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.75 }} className="lg:col-span-5 text-base sm:text-lg text-white/65 max-w-xl leading-relaxed" data-testid="hero-subtitle">
              {st("hero_subtitle")}
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.9 }} className="lg:col-span-7 flex flex-wrap lg:justify-end gap-3 sm:gap-4 [&_.btn-pill]:!text-[0.95rem]">
              <Link to="/menu" className="btn-pill btn-solid" onClick={() => events.menuView("hero")} data-testid="hero-menu-btn">
                {t("viewMenu")} <ArrowRight size={16} />
              </Link>
              <a href={s.maps_url || "#"} target="_blank" rel="noreferrer" onClick={events.directions} className="btn-pill btn-ghost" data-testid="hero-directions-btn">
                <Navigation size={16} /> {t("directions")}
              </a>
              <a href={phoneHref} onClick={events.phoneCall} className="btn-pill btn-ghost" data-testid="hero-call-btn">
                <Phone size={16} /> {t("callUs")}
              </a>
              {s.reservation_enabled && (
                <Link to="/rezervasyon" onClick={events.reservationClick} className="btn-pill btn-ghost !border-gold/40 !text-gold hover:!border-gold" data-testid="hero-reservation-btn">
                  {t("makeReservation")}
                </Link>
              )}
            </motion.div>
          </div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 1.2 }} className="mt-12 pt-6 border-t border-white/10 flex flex-wrap items-center gap-x-10 gap-y-4 text-[0.72rem] tracking-[0.2em] uppercase font-display text-white/50">
            {shortAddress && <span className="flex items-center gap-2"><MapPin size={13} className="text-gold" />{shortAddress}</span>}
            {s.hours_weekday && <span className="flex items-center gap-2"><Clock size={13} className="text-gold" />{s.hours_weekday}</span>}
            <span className="ml-auto hidden sm:flex items-center gap-3 normal-case tracking-normal text-white/40 text-xs"><span className="scroll-cue" /> Scroll</span>
          </motion.div>
        </motion.div>
      </section>

      {/* MARQUEE */}
      <div className="py-8 sm:py-10 border-y border-white/5 overflow-hidden bg-[#0a0807]" data-testid="editorial-marquee">
        <Marquee speed={38} gradient={false} autoFill>
          <span className="marquee-text mx-6">
            {t("marqueeText").split("·").filter((w) => w.trim()).map((w, i) => (
              <span key={i}>{i % 2 === 0 ? <b>{w.trim()}</b> : w.trim()}<span className="mx-5 text-gold">·</span></span>
            ))}
          </span>
        </Marquee>
      </div>

      {/* CAMPAIGNS */}
      {campaigns.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-16" data-testid="campaigns-section">
          {campaigns.map((c) => (
            <Reveal key={c.id} className="card-dark !border-gold/30 p-6 sm:p-8 flex items-start gap-4 mb-4">
              <Megaphone className="text-gold shrink-0 mt-1" size={22} />
              <div><h3 className="font-display font-bold text-xl">{c.title}</h3><p className="text-white/60 mt-2">{c.description}</p></div>
            </Reveal>
          ))}
        </section>
      )}

      {/* SIGNATURE STRIP */}
      <section className="py-24 sm:py-32 overflow-hidden" data-testid="featured-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
              <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
                {ft.slice(0, -1).join(" ")} <span className="font-serif-editorial italic font-normal text-gold-grad">{ft.slice(-1)}</span>
              </h2>
              <Link to="/menu" className="font-display text-xs font-bold uppercase tracking-[0.25em] text-white/50 hover:text-gold transition-colors flex items-center gap-2" data-testid="featured-view-all">
                {t("allMenu")} <ArrowUpRight size={15} />
              </Link>
            </div>
          </Reveal>
        </div>
        <div className="sig-strip">
          {featured.map((item, i) => (
            <Reveal key={item.id} delay={Math.min(i, 4) * 0.08}>
              <Link to="/menu" onClick={() => events.menuView("featured")}>
                <MenuItemCard item={item} index={i} variant="tall" />
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CATEGORY BENTO */}
      {menu.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24 sm:pb-32" data-testid="category-section">
          <Reveal>
            <p className="eyebrow mb-4"><span className="kicker-line" />{t("menu")}</p>
            <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mb-10"><span className="text-outline">{t("menuTitle")}</span></h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-4 sm:gap-5">
            {menu.map((c, i) => {
              const tile = CATEGORY_TILES[c.slug] || { img: c.items?.[0]?.image, span: "lg:col-span-4" };
              return (
                <Reveal key={c.id} delay={i * 0.08} className={tile.span}>
                  <Link to="/menu" onClick={() => events.menuView(c.name)} className="bento-tile h-full" data-testid={`home-cat-${c.slug}`}>
                    <img src={bust(c.image || tile.img || c.items?.[0]?.image)} alt={localName(c)} loading="lazy" />
                    <span className="bento-arrow"><ArrowUpRight size={16} /></span>
                    <div>
                      <h3 className="bento-title">{localName(c)}</h3>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </section>
      )}

      {/* MANIFESTO */}
      <section className="relative border-y border-white/5 bg-[#0a0807] overflow-hidden" data-testid="manifesto-section">
        <div className="glow-gold w-[34rem] h-[34rem] -right-40 -top-24 opacity-60" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32">
          <Reveal><p className="eyebrow mb-16"><span className="kicker-line" />{t("whyUs")}</p></Reveal>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-14 lg:gap-8">
            {manifesto.map((m, i) => (
              <Reveal key={m.num} delay={i * 0.12} className="relative pt-10 border-t border-white/10">
                <span className="mani-num text-gold-grad absolute -top-8 left-0 bg-[#0a0807] pr-4">{m.num}</span>
                <h3 className="font-display font-black text-2xl sm:text-3xl tracking-tight mt-4 mb-4">{t(m.titleKey)}</h3>
                <p className="text-white/55 leading-relaxed">{m.stKey ? st(m.stKey) : t(m.tKey)}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* COFFEE BAND */}
      <section className="band" data-testid="coffee-section">
        <img src={bust(img("coffee_image", "/images/latte.jpg"))} alt="Taze çekilmiş kahve" loading="lazy" />
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 w-full py-24">
          <Reveal className="max-w-xl">
            <p className="eyebrow mb-5 bg-gold !text-black !text-sm rounded-full px-5 py-2.5 w-fit"><span className="kicker-line !bg-black" />{t("coffeeEyebrow")}</p>
            <h2 className="font-serif-editorial italic text-5xl sm:text-6xl lg:text-7xl mb-6 leading-[0.95] bg-[#2b2b2e]/90 rounded-xl px-5 py-3 w-fit">{t("coffeeTitle")}</h2>
            <p className="text-white leading-relaxed mb-10 bg-[#2b2b2e]/90 rounded-xl px-5 py-4">{t("coffeeBody")}</p>
            <Link to="/menu" className="btn-pill btn-solid w-fit" data-testid="coffee-menu-btn">{t("coffeeBtn")} <ArrowRight size={15} /></Link>
          </Reveal>
        </div>
      </section>

      {/* DESSERT BAND */}
      <section className="band reverse" data-testid="dessert-section">
        <img src={bust(img("dessert_image", "/images/waffle.jpg"))} alt="Tatlı çeşitleri" loading="lazy" />
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 w-full py-24 flex lg:justify-end">
          <Reveal className="max-w-xl">
            <p className="eyebrow mb-5 bg-gold !text-black !text-sm rounded-full px-5 py-2.5 w-fit"><span className="kicker-line !bg-black" />{t("dessertEyebrow")}</p>
            <h2 className="font-serif-editorial italic text-5xl sm:text-6xl lg:text-7xl mb-6 leading-[0.95] bg-[#2b2b2e]/90 rounded-xl px-5 py-3 w-fit">{t("dessertTitle")}</h2>
            <p className="text-white leading-relaxed mb-10 bg-[#2b2b2e]/90 rounded-xl px-5 py-4">{t("dessertBody")}</p>
            <Link to="/menu" className="btn-pill btn-solid w-fit" data-testid="dessert-menu-btn">{t("dessertBtn")} <ArrowRight size={15} /></Link>
          </Reveal>
        </div>
      </section>

      {/* INSTAGRAM */}
      <section className="relative overflow-hidden border-t border-white/5" data-testid="instagram-section">
        <div className="glow-gold w-[40rem] h-[40rem] left-1/2 -translate-x-1/2 -bottom-60 opacity-70" />
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-28 text-center relative">
          <Reveal>
            <Instagram className="mx-auto text-gold mb-6" size={30} />
            <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter mb-5 leading-[0.95]">{t("igTitle")}</h2>
            <p className="text-white/55 mb-9 max-w-md mx-auto">{t("igBody")}</p>
            {s.instagram ? (
              <a href={s.instagram} target="_blank" rel="noreferrer" onClick={events.instagramClick} className="btn-pill btn-solid" data-testid="instagram-follow-btn"><Instagram size={16} /> {t("igBtn")}</a>
            ) : (<p className="text-xs text-white/30 italic">{t("addLater")}</p>)}
          </Reveal>
        </div>
      </section>
    </div>
  );
}
